import { useEffect, useState } from '/vendor/htm-preact.module.js';
import { cachedGet, useRead } from '/read.js';

/**
 * Which circle the numbers on screen are for.
 *
 * A rating, a frame count and a head-to-head record are all earned IN a
 * circle. Once that is true, every screen showing one of them has to agree on
 * which circle it means — otherwise the dial says 1564, the line under it ends
 * at 1500, and the record beside a name counts games from a different night.
 * That disagreement is the bug this whole idea exists to fix, so the choice
 * lives in one place rather than being made three times.
 *
 * Remembered, because it is a preference rather than a navigation step: you
 * play mostly in one circle and should not have to say so every time.
 */
const REMEMBERED = 'cueforge.gauge.circle';

/**
 * localStorage, without letting it take the screen down.
 *
 * Reading it throws outright in some private-browsing modes and wherever site
 * data is blocked, and writing throws when the quota is full. This is called
 * during render, so an exception there is not a lost preference — it is the
 * whole screen failing to draw, for a convenience nobody would miss.
 */
function remembered() {
  try {
    return localStorage.getItem(REMEMBERED) || '';
  } catch (e) {
    return '';
  }
}

function remember(id) {
  try {
    localStorage.setItem(REMEMBERED, id);
  } catch (e) { /* the choice still holds for this visit */ }
}

/**
 * One answer to "which circles am I in", shared by every screen that asks.
 *
 * It was fetched again on each of three tabs, and on two of them nothing else
 * could start until it came back — so a tab change cost two round trips of
 * which the first was for something already known. On a tailnet hop that
 * doubled the wait for Home, Leaders and Circles; Venues, which asks for
 * nothing first, was half the time.
 *
 * Dropped on ANY write, because a write may have changed it. Not a timer:
 * there is no interval that is both short enough to be right and long enough
 * to be worth having.
 */
export function myCircles() {
  return cachedGet('/api/me/circles');
}

export function useCircleChoice() {
  // Paints from what is already known, then confirms. The dial used to wait
  // for this before it could ask for a rating at all, so every tab change
  // cost two hops; now the second one starts immediately and the first is
  // usually already answered.
  const [mine, error] = useRead('/api/me/circles');
  const circles = mine ? mine.circles : (error ? [] : null);
  const [picked, setPicked] = useState(remembered);

  // DERIVED during render, not set from an effect. Going through state meant a
  // second render pass before anything downstream even knew which circle it
  // was for, and the board and the dial then needed a third to read it — three
  // passes, about two frames, for data already in hand. A remembered circle
  // you have since left is not a circle, so fall back to the first rather than
  // asking for a board that will answer 404.
  const chosen = circles
    ? (circles.some((c) => c.id === picked)
        ? picked : (circles[0] || {}).id || '')
    : picked;

  const choose = (id) => {
    remember(id);
    setPicked(id);
  };

  return [circles, chosen, choose];
}

/** The query string for a scoped read, or '' when there is no circle. */
export function scoped(circleId) {
  return circleId ? `?circle_id=${encodeURIComponent(circleId)}` : '';
}
